import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { PromptApiProvider, usePromptApi } from '../Services/PromptApiProvider';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { RodesPattern, RisenPattern, CoDPattern, SimplePrompt, SwarmPattern, RealtimePrompt } from '../Components/PromptPatternForm';
import { CustomInput, CustomSelect } from '../Components/CustomFormElements';
import { isEmpty } from '../utils/validationUtil';
import PageLoader from '../Components/PageLoader';
import EntityBoxWrapper from "../Components/EntityBox"
import './Prompt.css'

function PromptContent({ canEdit }) {
    const { organizationId, promptId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();

    const { getPromptById, createPrompt, updatePrompt } = usePromptApi();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isNew, setIsNew] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    const [name, setName] = useState();
    const [patternType, setPatternType] = useState();

    const [prompt, setPrompt] = useState()
    const [role, setRole] = useState()
    const [goal, setGoal] = useState()
    const [instruction, setInstruction] = useState()
    const [narrowing, setNarrowing] = useState()
    const [stepsToComplete, setStepsToComplete] = useState()
    const [checks, setChecks] = useState()
    const [details, setDetails] = useState()
    const [examples, setExamples] = useState()
    const [objective, setObjective] = useState()
    const [additionalGuidelines, setAdditionalGuidelines] = useState()
    const [benchmark, setBenchmark] = useState()
    const [recursion, setRecursion] = useState()
    const [speaker, setSpeaker] = useState()
    const [listener, setListener] = useState()

    const onCancel = () => {
        navigate(`/organizations/${organizationId}/prompts-hub`)
    };

    const onSave = async () => {
        if (isEmpty(name)) {
            showAlert('fail', 'Please enter a name for the prompt.');
            return;
        }
        if (isEmpty(patternType)) {
            showAlert('fail', 'Please select a prompt pattern.');
            return;
        }

        const definition = {}

        if (patternType === 'simple_prompt') {
            if (isEmpty(prompt)) {
                showAlert('fail', 'Please enter a prompt.');
                return;
            }

            definition["prompt"] = prompt;
        } else if (patternType === 'rodes') {
            if (isEmpty(role)) {
                showAlert('fail', 'Please enter a role.');
                return;
            }
            if (isEmpty(objective)) {
                showAlert('fail', 'Please enter an objective.');
                return;
            }
            if (isEmpty(details)) {
                showAlert('fail', 'Please enter details.');
                return;
            }
            if (isEmpty(examples)) {
                showAlert('fail', 'Please enter examples.');
                return;
            }
            if (isEmpty(checks)) {
                showAlert('fail', 'Please enter checks.');
                return;
            }

            definition["role"] = role;
            definition["objective"] = objective;
            definition["details"] = details;
            definition["examples"] = examples;
            definition["checks"] = checks;
        } else if (patternType === 'risen') {
            if (isEmpty(role)) {
                showAlert('fail', 'Please enter a role.');
                return;
            }
            if (isEmpty(instruction)) {
                showAlert('fail', 'Please enter an instruction.');
                return;
            }
            if (isEmpty(stepsToComplete)) {
                showAlert('fail', 'Please enter steps to complete.');
                return;
            }
            if (isEmpty(goal)) {
                showAlert('fail', 'Please enter a goal.');
                return;
            }
            if (isEmpty(narrowing)) {
                showAlert('fail', 'Please enter a narrowing.');
                return;
            }

            definition["role"] = role;
            definition["instruction"] = instruction;
            definition["goal"] = goal;
            definition["steps_to_complete"] = stepsToComplete;
            definition["narrowing"] = narrowing;
        } else if (patternType === 'chain_of_density') {
            if (isEmpty(instruction)) {
                showAlert('fail', 'Please enter an instruction.');
                return;
            }
            if (isEmpty(recursion)) {
                showAlert('fail', 'Please enter a recursion.');
                return;
            }
            if (isEmpty(benchmark)) {
                showAlert('fail', 'Please enter a benchmark.');
                return;
            }
            if (isEmpty(additionalGuidelines)) {
                showAlert('fail', 'Please enter additional guidelines.');
                return;
            }

            definition["instruction"] = instruction;
            definition["recursion"] = recursion;
            definition["benchmark"] = benchmark;
            definition["additional_guidelines"] = additionalGuidelines;
        } else if (patternType === 'swarm') {
            if (isEmpty(instruction)) {
                showAlert('fail', 'Please enter an instruction.');
                return;
            }
            if (isEmpty(role)) {
                showAlert('fail', 'Please enter a role.');
                return;
            }
            definition["instruction"] = instruction;
            definition["role"] = role;
        } else if (patternType === 'realtime') {
            if (isEmpty(speaker)) {
                showAlert('fail', 'Please enter a speaker.');
                return;
            }
            if (isEmpty(listener)) {
                showAlert('fail', 'Please enter a listener.');
                return;
            }
            definition["speaker"] = speaker;
            definition["listener"] = listener;
        }

        const data = {
            name: name,
            pattern_type: patternType,
            definition: definition
        }

        setLoading(true);

        try {
            if (isNew) {
                await createPrompt(data);
                showAlert('success', 'Prompt created successfully!');
            } else {
                await updatePrompt(promptId, data);
                showAlert('success', 'Prompt updated successfully!');
            }
            navigate(`/organizations/${organizationId}/prompts-hub`)
        }
        catch (error) {
            console.error('Error saving prompt:', error);
            setError(error);
            showAlert('fail', 'Failed to save the prompt.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const f = async () => {
            setLoading(true);
            try {
                if (promptId !== 'new-prompt') {
                    const response = await getPromptById(promptId);

                    setName(response.name);
                    setPatternType(response.pattern_type);
                    setPrompt(response.definition.prompt);
                    setRole(response.definition.role);
                    setObjective(response.definition.objective);
                    setDetails(response.definition.details);
                    setExamples(response.definition.examples);
                    setChecks(response.definition.checks);
                    setInstruction(response.definition.instruction);
                    setNarrowing(response.definition.narrowing);
                    setStepsToComplete(response.definition.steps_to_complete);
                    setAdditionalGuidelines(response.definition.additional_guidelines);
                    setBenchmark(response.definition.benchmark);
                    setRecursion(response.definition.recursion);
                    setGoal(response.definition.goal);
                    setSpeaker(response.definition.speaker);
                    setListener(response.definition.listener);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }
        f();
    }, [promptId]);

    useEffect(() => {
        setIsNew(promptId === 'new-prompt');
        setIsDisabled(!canEdit);
    }, [promptId])

    const renderEditForm = () => {
        switch (patternType) {
            case 'swarm':
                return (
                    <SwarmPattern
                        instructionValue={instruction}
                        setInstructionValue={setInstruction}
                        roleValue={role}
                        setRoleValue={setRole}
                        isDisabled={isDisabled}
                    />
                );
            case 'chain_of_density':
                return (
                    <CoDPattern
                        instructionValue={instruction}
                        setInstructionValue={setInstruction}
                        recursionValue={recursion}
                        setRecursionValue={setRecursion}
                        benchmarkValue={benchmark}
                        setBenchmarkValue={setBenchmark}
                        guidelinesValue={additionalGuidelines}
                        setGuidelinesValue={setAdditionalGuidelines}
                        isDisabled={isDisabled}
                    />
                );
            case 'risen':
                return (
                    <RisenPattern
                        goalValue={goal}
                        setGoalValue={setGoal}
                        instructionValue={instruction}
                        setInstructionValue={setInstruction}
                        narrowingValue={narrowing}
                        setNarrowingValue={setNarrowing}
                        roleValue={role}
                        setRoleValue={setRole}
                        stepsValue={stepsToComplete}
                        setStepsValue={setStepsToComplete}
                        isDisabled={isDisabled}
                    />
                );
            case 'rodes':
                return (
                    <RodesPattern
                        roleValue={role}
                        setRoleValue={setRole}    
                        objectiveValue={objective}
                        setObjectiveValue={setObjective}
                        detailsValue={details}
                        setDetailsValue={setDetails}
                        examplesValue={examples}
                        setExamplesValue={setExamples}
                        checkValue={checks}
                        setCheckValue={setChecks}                        
                        isDisabled={isDisabled}
                    />
                );
            case 'simple_prompt':
                return <SimplePrompt
                            promptValue={prompt}
                            setPromptValue={setPrompt}
                            isDisabled={isDisabled}
                        />;
            case 'realtime':
                return <RealtimePrompt
                    speakerValue={speaker}
                    setSpeakerValue={setSpeaker}
                    listenerValue={listener}
                    setListenerValue={setListener}
                    isDisabled={isDisabled}
                />;
            default:
                return <></>;
        }
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-6">
                    <h2 className='h2-title'>{name}</h2>
                    <div className='gap d-flex flex-row-to-col flex-column'>
                        <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                            <h4 className='h4-title'>Main Settings</h4>

                            <div className="input-block d-flex flex-row justify-between">
                                <div className="input-description">
                                    <div className="input-title">Name</div>
                                    <div className="small-font">Prompt template name</div>
                                </div>
                                <div className="align-wrapper d-flex align-center">
                                    <CustomInput
                                        id="prompt-name"
                                        label="Prompt Name"
                                        name="name"
                                        placeholder="Prompt name"
                                        icon={<i className="fa-solid fa-right-to-bracket"></i>}
                                        type="text"
                                        value={name}
                                        onChange={(id, value) => setName(value)}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>

                            <div className="input-block d-flex flex-row justify-between">
                                <div className="input-description">
                                    <div className="input-title">Prompt Pattern</div>
                                    <div className="small-font">Select prompt pattern</div>
                                </div>
                                <div className="align-wrapper d-flex align-center">
                                    <CustomSelect
                                        id="prompt-pattern"
                                        label="Pattern"
                                        options={[
                                            { value: 'simple_prompt', label: 'Simple prompt' },
                                            { value: 'rodes', label: 'RODES' },
                                            { value: 'risen', label: 'RISEN' },
                                            { value: 'chain_of_density', label: 'Chain of Density' },
                                            { value: 'swarm', label: 'Swarm' },
                                            { value: 'realtime', label: 'Realtime' }
                                        ]}
                                        value={patternType}
                                        onChange={(val) => setPatternType(val)}
                                        multiSelect={false}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>

                            {renderEditForm()}
                        </div>

                        <div className='buttons-container d-flex justify-end'>
                            <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                                Save
                            </Button>
                            <Button variant='danger' size='medium' onClick={onCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
                
                <div className="radius-yes p-2 bg-yes col-4">
                    <EntityBoxWrapper/>
                </div>
                    
            </div>
        </div>
    );
}

function Prompt({ canEdit }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <PromptApiProvider>
                    <PromptContent canEdit={canEdit} />
                </PromptApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default Prompt;